if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmit = this.onSubmitHandler.bind(this);
      }

      connectedCallback() {
        if (this.listeners) return;
        this.form = this.querySelector("form");
        if (!this.form) return;
        const variantInput = this.form.querySelector("[name=id]");
        if (variantInput) variantInput.disabled = false;
        this.listeners = new AbortController();
        this.form.addEventListener("submit", this.onSubmit, { signal: this.listeners.signal });
        this.minicart = document.querySelector("mini-cart");
        this.quickViewDrawer = this.closest("quick-view-drawer");
        this.eventContext = this.form.dataset.eventContext;
        this.hideErrors = this.dataset.hideErrors === "true";
      }

      disconnectedCallback() {
        this.listeners?.abort();
        this.listeners = null;
      }

      async onSubmitHandler(event) {
        event.preventDefault();
        const submitButton = event.submitter || this.querySelector('[type="submit"]');
        const buyNow = submitButton?.hasAttribute("data-cq-buy-now");
        this.minicart = document.querySelector("mini-cart");
        const drawer = this.minicart?.customDrawer;
        if (!submitButton || this.submitting || submitButton.disabled || submitButton.getAttribute("aria-disabled") === "true" || drawer?.mutating) {
          return;
        }
        const plan = this.form.querySelector("[data-cq-plan-select]");
        if (plan && !plan.disabled && !plan.reportValidity()) return;

        this.handleErrorMessage();
        this.submitting = true;
        const submitButtons = [...this.form.querySelectorAll('[type="submit"]')].map((button) => ({
          button,
          ariaDisabled: button.getAttribute("aria-disabled"),
        }));
        const spinner = submitButton.querySelector(".button-overlay-spinner");
        submitButtons.forEach(({ button }) => button.setAttribute("aria-disabled", "true"));
        submitButton.classList.add("loading");
        spinner?.classList.remove("hidden");
        let checkoutStarted = false;
        const finish = () => {
          this.submitting = false;
          submitButton.classList.remove("loading");
          submitButtons.forEach(({ button, ariaDisabled }) => {
            if (ariaDisabled === null) button.removeAttribute("aria-disabled");
            else button.setAttribute("aria-disabled", ariaDisabled);
          });
          spinner?.classList.add("hidden");
          drawer?.setBusy(false);
        };

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.minicart) {
          formData.set(
            "sections",
            [...new Set(this.minicart.getSectionsToRender().map((section) => section.section))].join(","),
          );
        }
        formData.set("sections_url", window.location.pathname);
        const json = this.jsonBody(formData);
        // r10 item 13: a ticked replacement subscription goes in the same request, after the product (snippets/
        // cq-replacement-offer.liquid). Only with the JSON body; a multipart form (a chosen file) adds the product alone.
        const extras = json ? this.extraItems() : [];
        let payload = json && (extras.length ? this.withItems(json, extras) : json);
        // Release 1 lane E (AJ 2026-10-10 18:07, "Skip duplicates, then checkout"): Buy now leaves out every item the cart
        // already holds (same variant, same selling plan or none, same add-on properties) and checks out the cart as it is.
        if (buyNow && payload) {
          const lines = await this.cartLines();
          const items = payload.items || [payload];
          const fresh = lines ? items.filter((item) => !lines.some((line) => this.sameItem(line, item))) : items;
          if (!fresh.length) {
            this.proceedToCheckout();
            window.addEventListener("pageshow", finish, { once: true });
            return;
          }
          if (fresh.length < items.length) payload = { ...payload, items: fresh };
        }
        if (payload) {
          config.headers["Content-Type"] = "application/json";
          config.body = JSON.stringify(payload);
        } else {
          config.body = formData;
        }
        // The request leaves first (the form data, selling plan and app inputs included, is already captured), then the
        // drawer opens with the product it is adding while Shopify answers; one round trip (/cart/add with the drawer
        // section) fills it. Opening the drawer closes the configure popup that may hold this form.
        const items = payload ? payload.items || [payload] : [{ id: formData.get("id"), quantity: formData.get("quantity") }];
        const request = this.sendAdd(config, items, drawer);
        let pending = null;
        try {
          if (!buyNow && typeof drawer?.beginAdd === "function") pending = drawer.beginAdd({ opener: submitButton, form: this.form, extras: extras.map((item) => item.preview) });
        } catch (error) {
          // The early drawer is an enhancement: the add still completes and opens the cart the usual way.
          console.error(error);
        }
        drawer?.setBusy(true);

        try {
          const { ok, answer: response } = await request.catch((error) => {
            console.error(error);
            return { ok: false, answer: null };
          });
          if (!ok) {
            const message = this.cartMessage(response);
            pending?.fail(message);
            this.handleErrorMessage(message);
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: formData.get("id"),
              errors: response?.errors || response?.description || message,
              message: response?.message || message,
            });
            return;
          }
          // Shopify has added it: nothing from here on reports a failed add (afterAdd).
          this.afterAdd(response, { buyNow, pending, drawer, submitButton });
          if (buyNow) {
            this.proceedToCheckout();
            checkoutStarted = true;
          }
        } finally {
          // Keep both actions locked during navigation, but restore them if Back revives this page from the cache.
          if (checkoutStarted) window.addEventListener("pageshow", finish, { once: true });
          else finish();
        }
      }

      // The add request (AJ 2026-10-11 09:33, iPhone: "there was an error while updating your cart please try again i did
      // shortly and it worked"). That generic message only came from an add that never got a readable answer: the POST
      // failing in transit (Safari's "Load failed" when the connection the page held went stale, which Safari does not
      // retry for a POST) or a busy answer from Shopify's cart (429, an HTML page). Both pass in a moment, so the add is
      // tried once more after a short pause. A request that failed in transit or on a server error may still have reached
      // Shopify, so the cart is read first and the add is only sent again when it is not there (never a double add).
      // Resolves to { ok, answer }, answer being Shopify's JSON (the added line, or { items }) or its error.
      async sendAdd(config, items, drawer) {
        const before = Number.isFinite(drawer?.itemCount) ? drawer.itemCount : this.minicart?.currentItemCount;
        const send = () => fetch(`${routes.cart_add_url}`, config).then((result) => this.readAdd(result), () => ({ lost: true }));
        let reply = await send();
        if (reply.lost || reply.busy) {
          await new Promise((resolve) => setTimeout(resolve, reply.wait || 800));
          const landed = reply.busy ? null : await this.addLanded(before, items);
          reply = landed ? { ok: true, answer: landed } : await send();
        }
        return reply.lost || reply.busy ? { ok: false, answer: reply.answer || null } : reply;
      }

      // { ok, answer } for Shopify's reply, or { busy, wait } (429: the add was refused, safe to send again) or { lost } (a
      // server error: it may have been added). An OK reply whose body cannot be read is still an add; the drawer then
      // loads its own markup (cq-cart-drawer.js).
      async readAdd(result) {
        const answer = await result.json().catch(() => null);
        if (result.status === 429) {
          const seconds = Number(result.headers.get("Retry-After"));
          return { busy: true, answer, wait: seconds > 0 ? Math.min(seconds * 1000, 3000) : 800 };
        }
        if (result.status >= 500) return { lost: true, answer };
        if (!answer) return { ok: result.ok, answer: result.ok ? {} : null };
        return { ok: result.ok && !answer.status && !answer.errors, answer };
      }

      // After a failed request: the cart when it already holds this add (its count went up by the added quantity since the
      // drawer last drew it), with the added variants as its items; null when it does not, or when that cannot be told.
      async addLanded(before, items) {
        if (!Number.isFinite(before)) return null;
        try {
          const response = await fetch(`${routes.cart_url}.js`, { cache: "no-store", headers: { Accept: "application/json" } });
          const cart = response.ok ? await response.json() : null;
          const added = items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
          if (!cart || !(cart.item_count >= before + added)) return null;
          const ids = new Set(items.map((item) => String(item.id)));
          return { ...cart, items: cart.items.filter((line) => ids.has(String(line.variant_id))) };
        } catch (error) {
          return null;
        }
      }

      // Shopify's own words for a refused add (a 422: sold out, more than in stock, a plan the variant does not take).
      cartMessage(answer) {
        const errors = answer?.errors;
        const listed = errors && typeof errors === "object" ? Object.values(errors).flat().find((value) => typeof value === "string") : "";
        return (typeof answer?.description === "string" && answer.description) || (typeof errors === "string" && errors) || listed || cartStrings.error;
      }

      // Everything after a successful add. Each step stands alone: a drawer that cannot draw Shopify's answer loads its markup
      // again (instead of the add reporting an error for an item that is in the cart), and a listener's failure is logged.
      afterAdd(response, { buyNow, pending, drawer, submitButton }) {
        const step = (run) => {
          try {
            run();
          } catch (error) {
            console.error(error);
          }
        };
        if (!buyNow) {
          let drawn = false;
          step(() => {
            try {
              drawn = this.minicart?.renderContents(response) !== false;
            } finally {
              // The answer did not fill the drawer: it loads its own markup, and a failure there is not an add error.
              if (drawer && !drawn) {
                drawer.unrenderedAdd = true;
                drawer.scheduleRefresh?.(false);
              }
            }
          });
          step(() => pending?.done(response));
        }

        if (!buyNow && (!this.minicart || (!drawer && window.themeSettings?.redirectToCart))) {
          location.href = window.routes.cart_url;
        } else if (!buyNow && !pending?.dismissed) {
          // Left closed when the shopper closed the cart while it was adding.
          step(() => {
            if (this.quickViewDrawer && !drawer) {
              this.quickViewDrawer.close();
              // Immediately re-add the 'drawer-open' class to avoid
              // the drawer backdrop from flashing.
              document.body.classList.add("drawer-open");
              setTimeout(() => this.minicart.open(), 200);
            } else {
              this.minicart.open(submitButton);
            }
          });
        }

        // A request with several items answers with { items: [...] }; a single add answers with the line itself.
        const addedItems = Array.isArray(response.items) ? response.items : [response];
        document.dispatchEvent(
          new CustomEvent("cart:item-add", {
            detail: {
              items: addedItems,
              cartData: response,
              opener: submitButton,
              context: this.eventContext,
              ...(buyNow ? { checkout: true } : {}),
            },
          }),
        );
        step(() =>
          publish(PUB_SUB_EVENTS.cartItemAdd, {
            source: this.eventContext,
            items: addedItems,
            cartData: response,
            opener: submitButton,
            ...(buyNow ? { checkout: true } : {}),
          }),
        );

        if (window.BOLD && BOLD.common && BOLD.common.eventEmitter && typeof BOLD.common.eventEmitter.emit === "function") {
          setTimeout(function () {
            BOLD.common.eventEmitter.emit("BOLD_COMMON_cart_loaded");
          }, 2000);
        }
      }

      proceedToCheckout() {
        // Shopify's documented cart checkout contract, using the locale-aware route already supplied by Liquid.
        const checkoutForm = document.createElement("form");
        checkoutForm.method = "post";
        checkoutForm.action = window.routes.cart_url;
        checkoutForm.hidden = true;
        const checkoutButton = document.createElement("button");
        checkoutButton.type = "submit";
        checkoutButton.name = "checkout";
        checkoutButton.value = "checkout";
        checkoutForm.append(checkoutButton);
        document.body.append(checkoutForm);
        checkoutForm.requestSubmit(checkoutButton);
      }

      // The add goes as JSON, the way the live store's cart app sends it. A multipart body escapes a double quote in
      // a field name as %22 (the HTML form encoding rules) and Shopify keeps that escape in the line's property name,
      // so Bold's "Leak Detector Smart Valve (1/4" or 3/8")" reached the cart and checkout as "(1/4%22 or 3/8%22)";
      // multipart also dropped Bold's empty _sc* properties. Every field goes: properties[...] and attributes[...] as
      // objects, the rest as they are (Shopify ignores fields it does not use, as it did in the multipart body).
      // Returns null, and the form goes as multipart as before, when it holds a chosen file or another nested field.
      jsonBody(formData) {
        const body = { properties: {} };
        let attributes = null;
        for (const [name, value] of formData.entries()) {
          if (typeof value !== "string") {
            if (value.size || value.name) return null;
            continue;
          }
          const property = /^properties\[(.*)\]$/s.exec(name);
          const attribute = /^attributes\[(.*)\]$/s.exec(name);
          if (property) body.properties[property[1]] = value;
          else if (attribute) (attributes ||= {})[attribute[1]] = value;
          else if (name.includes("[")) return null;
          else if (name === "id" || name === "quantity" || name === "selling_plan") {
            if (value !== "") body[name] = /^\d+$/.test(value) ? Number(value) : value;
          } else body[name] = value;
        }
        if (attributes) body.attributes = attributes;
        return body;
      }

      // r10 item 13: ticked extra lines in this form ([data-cq-extra-item]: the replacement subscription), each one item
      // of quantity 1 (or its data-quantity) with its selling plan; one inside a hidden block (no cartridge for the chosen option) is left out.
      // preview is for the side cart's placeholder line only and is not sent.
      extraItems() {
        return [...this.form.querySelectorAll("[data-cq-extra-item]:checked")]
          .filter((input) => !input.closest("[hidden]") && /^\d+$/.test(input.dataset.variantId || ""))
          .map((input) => {
            const item = { id: Number(input.dataset.variantId), quantity: Math.max(1, Number(input.dataset.quantity) || 1) };
            if (/^\d+$/.test(input.dataset.sellingPlan || "")) item.selling_plan = Number(input.dataset.sellingPlan);
            Object.defineProperty(item, "preview", { value: { id: item.id, title: input.dataset.title || "", src: input.dataset.image || "", plan: Boolean(item.selling_plan) } });
            return item;
          });
      }

      // The cart's lines for Buy now's duplicate check, or null when the cart cannot be read (Buy now then adds as before).
      async cartLines() {
        try {
          const response = await fetch(`${routes.cart_url}.js`, { cache: "no-store", headers: { Accept: "application/json" } });
          if (!response.ok) return null;
          const cart = await response.json();
          return Array.isArray(cart.items) ? cart.items : null;
        } catch (error) {
          return null;
        }
      }

      // True when a cart line is the same item as a request item: same variant, same selling plan (or none on both) and
      // the same add-on properties. The cart drops blank properties, so they are left out on both sides, and Bold's
      // builder ids (_scBuilderId, _boldBuilderId) are left out because Bold draws a new one for every add of the same
      // choices. Quantity is not compared: the line already there goes to checkout as it is.
      sameItem(line, item) {
        if (String(line.variant_id) !== String(item.id)) return false;
        if (String(line.selling_plan_allocation?.selling_plan?.id || "") !== String(item.selling_plan || "")) return false;
        const choices = (properties) =>
          JSON.stringify(
            Object.entries(properties || {})
              .filter(([name, value]) => name !== "_scBuilderId" && name !== "_boldBuilderId" && value != null && String(value).trim() !== "")
              .map(([name, value]) => [name, String(value).trim()])
              .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
          );
        return choices(line.properties) === choices(item.properties);
      }

      // The product (with its properties and plan) as the first item of an items request; the section fields stay at the top.
      withItems(json, extras) {
        const { id, quantity, selling_plan: sellingPlan, properties, sections, sections_url: sectionsUrl, attributes } = json;
        const item = { id, quantity: quantity || 1, properties };
        if (sellingPlan) item.selling_plan = sellingPlan;
        const body = { items: [item, ...extras] };
        if (sections) body.sections = sections;
        if (sectionsUrl) body.sections_url = sectionsUrl;
        if (attributes) body.attributes = attributes;
        return body;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector(".product-form-error-message-wrapper");
        if (!this.errorMessageWrapper && errorMessage && this.form) {
          this.errorMessageWrapper = document.createElement("div");
          this.errorMessageWrapper.className = "product-form-error-message-wrapper form-status form-status-error";
          this.errorMessageWrapper.setAttribute("role", "alert");
          this.form.append(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(".product-form-error-message") || this.errorMessageWrapper;

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
